<template>
  <v-dialog :value="value" max-width="680" @input="$emit('input', $event)">
    <v-card>
      <v-toolbar color="primary" dark flat dense>
        <v-toolbar-title>{{
          $t("reports.aiCredit.manageCredits")
        }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-5">
        <h3 class="black--text mb-1">
          {{ $t("reports.aiCredit.manageTitle") }}
        </h3>
        <p class="grey--text mb-4">{{ $t("reports.aiCredit.manageDesc") }}</p>

        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          class="mb-3"
        />

        <v-row class="mb-2">
          <v-col cols="6">
            <div class="grey--text text-caption">
              {{ $t("reports.aiCredit.colCooperative") }}
            </div>
            <div class="font-weight-bold">{{ coopName }}</div>
          </v-col>
        </v-row>

        <v-divider class="mb-3" />

        <v-radio-group v-model="mode" hide-details class="mt-0">
          <!-- One-time top-up -->
          <v-radio :value="'one_time'">
            <template v-slot:label>
              <span class="black--text">{{
                $t("reports.aiCredit.oneTimeTopup")
              }}</span>
            </template>
          </v-radio>
          <div class="pl-8 mb-4 mt-2">
            <label class="d-block mb-1 grey--text text-caption">{{
              $t("reports.aiCredit.addCredits")
            }}</label>
            <v-text-field
              v-model.number="oneTimeAmount"
              :disabled="mode !== 'one_time'"
              type="number"
              min="0"
              :suffix="$t('reports.aiCredit.credits')"
              placeholder="2000"
              outlined
              dense
              hide-details
              style="max-width: 360px"
            />
          </div>

          <!-- Recurring -->
          <v-radio :value="'recurring'">
            <template v-slot:label>
              <span class="black--text">{{
                $t("reports.aiCredit.recurringAllocation")
              }}</span>
            </template>
          </v-radio>
          <div class="pl-8 mt-2">
            <label class="d-block mb-1 grey--text text-caption">{{
              $t("reports.aiCredit.addCredits")
            }}</label>
            <v-text-field
              v-model.number="recurringAmount"
              :disabled="mode !== 'recurring'"
              type="number"
              min="0"
              :suffix="$t('reports.aiCredit.credits')"
              placeholder="0"
              outlined
              dense
              hide-details
              style="max-width: 360px"
              class="mb-3"
            />
            <label class="d-block mb-1 grey--text text-caption">{{
              $t("reports.aiCredit.frequency")
            }}</label>
            <v-btn-toggle
              v-model="frequency"
              mandatory
              dense
              :disabled="mode !== 'recurring'"
            >
              <v-btn small value="monthly">{{
                $t("reports.aiCredit.planMonthly")
              }}</v-btn>
              <v-btn small value="yearly">{{
                $t("reports.aiCredit.planYearly")
              }}</v-btn>
            </v-btn-toggle>
          </div>
        </v-radio-group>
      </v-card-text>

      <v-card-actions class="pb-4 px-4">
        <v-spacer />
        <v-btn outlined color="primary" @click="close">{{
          $t("reports.aiCredit.cancel")
        }}</v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!valid || loading"
          :loading="saving"
          @click="submit"
        >
          {{ $t("reports.aiCredit.submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";

export default {
  name: "ManageCreditsDialog",
  props: {
    value: { type: Boolean, default: false },
    cooperative: { type: Object, default: null },
  },
  data() {
    return {
      mode: "one_time",
      oneTimeAmount: null,
      recurringAmount: null,
      frequency: "monthly",
      saving: false,
      loading: false,
    };
  },
  computed: {
    coopName() {
      return this.cooperative ? this.cooperative.cooperative : "";
    },
    valid() {
      if (this.mode === "one_time") return Number(this.oneTimeAmount) > 0;
      return Number(this.recurringAmount) > 0 && !!this.frequency;
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    async load() {
      this.mode = "one_time";
      this.oneTimeAmount = null;
      this.recurringAmount = null;
      this.frequency = "monthly";
      if (!this.cooperative) return;
      this.loading = true;
      try {
        const env = await AiCreditService.getPoolSettings(
          this.cooperative.organization
        );
        const plan = env && env.data && env.data.plan;
        if (plan) {
          // Autofill the form with the organization's current plan.
          if (plan.planType === "recurring") {
            this.mode = "recurring";
            this.recurringAmount = plan.amount;
            this.frequency = plan.frequency || "monthly";
          } else {
            this.mode = "one_time";
            this.oneTimeAmount = plan.amount;
          }
        }
      } catch (e) {
        // non-fatal; start fresh
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.valid) return;
      this.saving = true;
      try {
        const payload =
          this.mode === "one_time"
            ? { planType: "one_time", amount: Number(this.oneTimeAmount) }
            : {
                planType: "recurring",
                amount: Number(this.recurringAmount),
                frequency: this.frequency,
              };
        const data = await AiCreditService.savePoolSettings(
          this.cooperative.organization,
          payload
        );
        if (data && data.success === false) throw new Error(data.message);
        this.$emit("saved");
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.saveFailed"),
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
